import { EventIds, PerformanceEvent, NavigationTimingPolyfillEntry } from '@fpkg/stats-core';

export interface PerformanceOpt {
  // 超过最大时间，超过不上报
  max_duration?: number;
  // 是否是本地页面，如丰声微应用
  is_local_page?: boolean;
  // 是否开启 LCP 请求链路上报
  whether_lcp_with_resource_info?: boolean;
  // LCP 上报请求链路信息的阈值
  lcp_with_resource_info_threshold?: number;
}

export function captureWebVitalsData() {}

const getNavigationEntryFromPerformanceTiming = (): NavigationTimingPolyfillEntry => {
  // Really annoying that TypeScript errors when using `PerformanceTiming`.
  const { timing } = performance;
  const navigationEntry: { [key: string]: number | string } = {
    entryType: 'navigation',
    startTime: 0,
  };

  Object.keys(timing).forEach((key) => {
    if (key !== 'navigationStart' && key !== 'toJSON') {
      navigationEntry[key] = Math.max((timing[key as keyof PerformanceTiming] as number) - timing.navigationStart, 0);
    }
  });
  return navigationEntry as unknown as NavigationTimingPolyfillEntry;
};

export function capturePerformanceData(option: PerformanceOpt): PerformanceEvent | null {
  const timesInfo: PerformanceEvent = { event: EventIds.WebPerformance };

  try {
    const ne = performance.getEntriesByType('navigation');
    const navigationEntry =
      ne && ne[0] ? (ne[0] as PerformanceNavigationTiming) : getNavigationEntryFromPerformanceTiming();

    // 上一个页面卸载时间
    timesInfo.unload_time = Math.round(navigationEntry.unloadEventEnd - navigationEntry.unloadEventStart);
    // 重定向时间
    timesInfo.redirect_time = Math.round(navigationEntry.redirectEnd - navigationEntry.redirectStart);
    // appcache
    timesInfo.appcache_time = Math.round(navigationEntry.domainLookupStart - navigationEntry.fetchStart);
    // DNS解析，DNS查询耗时
    timesInfo.dns_time = Math.round(navigationEntry.domainLookupEnd - navigationEntry.domainLookupStart);
    // tcp连接耗时
    timesInfo.tcp_time = Math.round(navigationEntry.connectEnd - navigationEntry.connectStart);
    // ssl
    timesInfo.ssl_time = Math.round(navigationEntry.connectEnd - navigationEntry.secureConnectionStart);
    // request请求耗时
    timesInfo.req_time = Math.max(Math.round(navigationEntry.responseEnd - navigationEntry.requestStart), 0);
    // waiting(TTFB):等待第一个字节时间
    timesInfo.waiting_ttfb_time = Math.round(navigationEntry.responseStart - navigationEntry.requestStart);
    // TTFB:读取页面第一个字节的时间
    timesInfo.ttfb_time = Math.round(navigationEntry.responseStart);
    // FP:白屏时间,此时页面资源加载完成，即将进入渲染环节
    // @ts-ignore
    timesInfo.fp_time = Math.round(navigationEntry.domInteractive || navigationEntry.domLoading);
    // 解析dom树耗时
    timesInfo.dom_time = Math.round(navigationEntry.domComplete - navigationEntry.domInteractive);
    // FCP:首屏时间,此时页面DOM树已经解析完成并且显示内容
    timesInfo.fcp_time = Math.round(navigationEntry.domComplete);
    // 从开始至load总耗时,load事件调用时间，这个时候vue dom渲染完成
    timesInfo.load_time = Math.round(navigationEntry.loadEventEnd);

    // In some cases the value reported is negative or is larger
    // than the current page time. Ignore these cases:
    // https://github.com/GoogleChrome/web-vitals/issues/137
    // https://github.com/GoogleChrome/web-vitals/issues/162// https://github.com/GoogleChrome/web-vitals/issues/275
    const isBadTTFB = timesInfo.ttfb_time < 0 || timesInfo.ttfb_time > performance.now();
    const isBadRespStart = option.is_local_page
      ? navigationEntry.responseStart < 0 || navigationEntry.responseStart > performance.now()
      : navigationEntry.responseStart <= 0 || navigationEntry.responseStart > performance.now();

    if (isBadTTFB || isBadRespStart) {
      return null;
    }

    // 过滤tcp和load时间超过最大时间
    if (timesInfo.load_time > option.max_duration! || timesInfo.tcp_time > option.max_duration!) {
      return null;
    }
  } catch (err) {
    console.error(err);
    return null;
  }

  return timesInfo;
}

export function formateResourceInfo(): Array<{ name: string; value: string }> {
  const result: Array<{ name: string; value: string }> = [];
  try {
    const resourceInfo = performance.getEntriesByType('resource');
    if (resourceInfo && resourceInfo.length) {
      return resourceInfo.map((item) => ({
        name: item.name,
        value: JSON.stringify({
          connectStart: item.connectStart,
          connectEnd: item.connectEnd,
          domainLookupEnd: item.domainLookupEnd,
          domainLookupStart: item.domainLookupStart,
          duration: item.duration,
          fetchStart: item.fetchStart,

          redirectEnd: item.redirectEnd,
          redirectStart: item.redirectStart,
          requestStart: item.requestStart,
          responseEnd: item.responseEnd,
          responseStart: item.responseStart,
          // @ts-ignore
          responseStatus: item.responseStatus,
          transferSize: item.transferSize,
          startTime: item.startTime,
        }),
      }));
    }
  } catch (e) {
    console.error(e);
  }
  return result;
}
